import React, { useContext, useState, useEffect } from 'react'
import {
  FormControlLabel,
  FormLabel,
  LinearProgress,
  Radio,
  RadioGroup,
  Grid,
  Autocomplete,
  TextField
} from '@mui/material'
import axios from 'axios'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { format } from 'date-fns'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import fi from 'date-fns/locale/fi'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AuthContext } from '../../../context/AuthProvider'
import {
  ErrorMessage,
  LaakariButton,
  ShadowContainer,
  SubHeaderH3
} from '../../../components/styled/uiComponents'
import { MetadataContext } from '../../../context'
import { Job, UpdateJobFormData } from '../../../types/job'
import { buildApiUrl } from '../../../utils/utils'
import { isSessionInvalidError } from '../../../utils/error'

// Helper function to parse date strings in dd.MM.yyyy format (with or without leading zeros)
const parseFlexibleDate = (dateString: string): Date | null => {
  if (!dateString || typeof dateString !== 'string') {
    return null
  }

  // Remove extra whitespace
  const cleaned = dateString.trim()

  // Try to match dd.MM.yyyy or d.M.yyyy format
  const match = cleaned.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (match) {
    const day = parseInt(match[1], 10)
    const month = parseInt(match[2], 10)
    const year = parseInt(match[3], 10)

    // Create date and validate
    const date = new Date(year, month - 1, day)
    if (
      !isNaN(date.getTime()) &&
      date.getDate() === day &&
      date.getMonth() === month - 1 &&
      date.getFullYear() === year
    ) {
      return date
    }
  }

  return null
}

const collator = new Intl.Collator('fi-FI', {
  sensitivity: 'base',
  numeric: true
})
const ORDER = [
  'Hyvinvointialue',
  'Ammatinharjoittaja',
  'Yksityinen (työsuhde)',
  'Yliopisto',
  'Valtio',
  'Muu'
] as const
const orderIndex = new Map(ORDER.map((v, i) => [v.toLowerCase(), i]))

const formatJobValue = (job: Job): UpdateJobFormData => {
  const {
    sll_tyoaika,
    els_alkupvm,
    els_loppupvm,
    tyosuhdetoimiala,
    tyosuhdesektori,
    els_paatoimi,
    ammattinimike,
    els_soteorganisaatio,
    sll_tyosuhde_sijaintikunta
  } = job
  // Käsittele molemmat nimet: sijaintikunta tai sll_tyosuhde_sijaintikunta
  const sijaintikunta = sll_tyosuhde_sijaintikunta || (job as any).sijaintikunta
  return {
    els_paatoimi: els_paatoimi ? 'yes' : 'no',
    sll_tyoaika,
    els_alkupvm,
    els_loppupvm,
    els_tyosuhdetoimiala: tyosuhdetoimiala ? JSON.stringify(tyosuhdetoimiala) : undefined,
    els_ammattinimike: ammattinimike ? JSON.stringify(ammattinimike) : undefined,
    els_tyosuhdesektori: tyosuhdesektori ? JSON.stringify(tyosuhdesektori) : undefined,
    els_soteorganisaatio: els_soteorganisaatio ? JSON.stringify(els_soteorganisaatio) : undefined,
    sll_tyosuhde_sijaintikunta: sijaintikunta?.els_koodiid || undefined
  }
}

interface Props {
  job: Job
  closeDialog: () => void
}

const updateJobSchema = yup.object().shape({
  els_alkupvm: yup.string().nullable(),
  els_loppupvm: yup
    .string()
    .nullable()
    .test(
      'end-after-start',
      'Työsuhteen loppupäivän tulee olla alkupäivän jälkeen',
      function (end) {
        const start = this.parent?.els_alkupvm
        if (!start || !end) {
          return true
        }
        const re = /^\d{4}-\d{2}-\d{2}$/
        if (!re.test(start) || !re.test(end)) {
          return true
        }
        return end > start
      }
    )
})

const UpdateJobForm: React.FC<Props> = ({ job, closeDialog }) => {
  const defaultValues = formatJobValue(job)

  const { metaData } = useContext(MetadataContext)
  const { bearerToken, evaluateToken, handleLogout } = useContext(AuthContext)
  const [submitError, setSubmitError] = useState(false)
  const [searchQuery, setSearchQuery] = useState<string>('')
  const navigate = useNavigate()
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { isSubmitting, errors }
  } = useForm<UpdateJobFormData>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    resolver: yupResolver(updateJobSchema)
  })

  const sector = watch('els_tyosuhdesektori')
  const selectedJob = watch('els_soteorganisaatio')
  const employmentType = watch('tyosuhdeluonne')
  const elsLoppupvm = watch('els_loppupvm')
  const elsAlkupvm = watch('els_alkupvm')

  useEffect(() => {
    if (employmentType && employmentType !== 'maaraaikainen' && setValue) {
      setValue('els_loppupvm', null)
    }
  }, [employmentType, setValue])

  // Kun loppupäivä on asetettu, vaihda työsuhteen luonne määräaikaiseksi
  useEffect(() => {
    if (elsLoppupvm) {
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
      if (typeof elsLoppupvm === 'string' && dateRegex.test(elsLoppupvm)) {
        setValue('tyosuhdeluonne', 'maaraaikainen')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elsLoppupvm])

  // Re-validate els_loppupvm when els_alkupvm changes
  useEffect(() => {
    if (elsAlkupvm && /^\d{4}-\d{2}-\d{2}$/.test(elsAlkupvm)) {
      trigger('els_loppupvm')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elsAlkupvm])

  const onSubmit = async (data: UpdateJobFormData) => {
    try {
      if (submitError) {
        setSubmitError(false)
      }

      // Convert JSON strings to API format
      const apiData: any = {
        els_paatoimi: data.els_paatoimi === 'yes',
        els_alkupvm: data.els_alkupvm,
        els_tyosuhdeid: job.els_tyosuhdeid
      }

      if (data.els_tyosuhdetoimiala) {
        if (typeof data.els_tyosuhdetoimiala === 'string') {
          try {
            const parsed = JSON.parse(data.els_tyosuhdetoimiala)
            apiData.els_tyosuhdetoimiala = parsed.attributevalue
          } catch (e) {
            // If parsing fails, try as number
            apiData.els_tyosuhdetoimiala = Number(data.els_tyosuhdetoimiala) || 0
          }
        } else {
          apiData.els_tyosuhdetoimiala = data.els_tyosuhdetoimiala
        }
      }

      if (data.els_ammattinimike) {
        if (typeof data.els_ammattinimike === 'string' && data.els_ammattinimike.startsWith('{')) {
          try {
            const parsed = JSON.parse(data.els_ammattinimike)
            apiData.els_ammattinimike = parsed.els_koodiid
          } catch (e) {
            apiData.els_ammattinimike = data.els_ammattinimike
          }
        } else {
          apiData.els_ammattinimike = data.els_ammattinimike
        }
      }

      // Handle els_loppupvm: set to null if employment type is "toistaiseksi_voimassa"
      if (data.tyosuhdeluonne === 'toistaiseksi_voimassa') {
        apiData.els_loppupvm = null
      } else if (data.els_loppupvm && data.els_loppupvm !== '') {
        apiData.els_loppupvm = data.els_loppupvm
      }
      /*if(data?.els_alkupvm > data.els_loppupvm){

      }*/

      if (data.sll_tyoaika) {
        apiData.sll_tyoaika = data.sll_tyoaika
      }

      // if (data.tyosuhdeluonne) {
      //   apiData.tyosuhdeluonne = data.tyosuhdeluonne
      // }

      if (data.els_soteorganisaatio) {
        if (
          typeof data.els_soteorganisaatio === 'string' &&
          data.els_soteorganisaatio.startsWith('{')
        ) {
          try {
            const parsed = JSON.parse(data.els_soteorganisaatio)
            apiData.els_soteorganisaatio = parsed.els_soteorganisaatiorekisteriid
          } catch (e) {
            apiData.els_soteorganisaatio = data.els_soteorganisaatio
          }
        } else {
          apiData.els_soteorganisaatio = data.els_soteorganisaatio
        }
      }

      if (data.sll_tyosuhde_sijaintikunta) {
        apiData.sll_tyosuhde_sijaintikunta = data.sll_tyosuhde_sijaintikunta
      }

      const url = buildApiUrl(`employment`)
      await axios.put(url, apiData, {
        headers: { 'x-tietoni-token': `Bearer ${bearerToken}` }
      })
      evaluateToken()
      closeDialog()
      // TODO append updated employment data to UserContext
    } catch (error) {
      setSubmitError(true)
      console.log('error when submitting form', error)
      if (isSessionInvalidError(error)) {
        closeDialog()
        toast.error('Istunto on päättynyt. Kirjaudu uudelleen sisään.')
        handleLogout(() => navigate('/'))
      }
    }
  }

  return metaData ? (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ShadowContainer style={{ marginBottom: 8 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <SubHeaderH3>Työpaikka</SubHeaderH3>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Työnantajasektori</FormLabel>
                  <Controller
                    name='els_tyosuhdesektori'
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        value={
                          value && typeof value === 'string'
                            ? (() => {
                                try {
                                  return JSON.parse(value)
                                } catch {
                                  return null
                                }
                              })()
                            : null
                        }
                        options={
                          metaData?.sektorit
                            ?.filter((sector: any) => sector.value !== 'Kunta')
                            .sort((a: any, b: any) => {
                              const av = a?.value?.trim().toLowerCase() ?? ''
                              const bv = b?.value?.trim().toLowerCase() ?? ''
                              const ia = orderIndex.get(av) ?? Number.POSITIVE_INFINITY
                              const ib = orderIndex.get(bv) ?? Number.POSITIVE_INFINITY
                              if (ia !== ib) {
                                return ia - ib
                              }
                              if (!av && !bv) {
                                return 0
                              }
                              if (!av) {
                                return 1
                              }
                              if (!bv) {
                                return -1
                              }
                              return av.localeCompare(bv, 'fi-FI', { sensitivity: 'base' })
                            }) || []
                        }
                        getOptionLabel={(option: any) =>
                          typeof option === 'object' ? option.value : ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.attributevalue === value.attributevalue
                        }
                        filterOptions={(options, { inputValue }) => {
                          const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                          return options.filter(option =>
                            option.value.toLowerCase().includes(cleanedInput.toLowerCase())
                          )
                        }}
                        onChange={(event, newValue) => {
                          if (newValue) {
                            const cleanedSector = JSON.stringify({
                              attributevalue: newValue.attributevalue,
                              value: newValue.value.trim().replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                            })
                            onChange(cleanedSector)
                            setSearchQuery('')
                            if (setValue) {
                              setValue('els_soteorganisaatio', '')
                            }
                          } else {
                            onChange('')
                            setSearchQuery('')
                            if (setValue) {
                              setValue('els_soteorganisaatio', '')
                            }
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            placeholder='Valitse tai kirjoita hakuun...'
                            fullWidth
                            variant='outlined'
                            size='small'
                          />
                        )}
                        noOptionsText='Ei vaihtoehtoja'
                      />
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container item>
                <Grid item xs={12} md={6}>
                  <FormLabel>Työpaikka</FormLabel>
                  <Controller
                    name='els_soteorganisaatio'
                    control={control}
                    defaultValue=''
                    render={({ field: { onChange, value } }) => {
                      let parsedValue = null
                      try {
                        if (value && typeof value === 'string' && value.trim() !== '') {
                          parsedValue = JSON.parse(value)
                        }
                      } catch (e) {
                        parsedValue = null
                      }

                      return (
                        <Autocomplete
                          key={sector} // Force re-render when sector changes
                          value={parsedValue}
                          options={
                            sector && typeof sector === 'string'
                              ? (() => {
                                  if (!metaData?.tyopaikat) {
                                    return []
                                  }

                                  const allWorkplaces = metaData.tyopaikat

                                  try {
                                    const sectorData = JSON.parse(sector)
                                    const attributevalue = +sectorData.attributevalue

                                    const filteredWorkplaces = allWorkplaces
                                      .filter((workplace: any) => {
                                        const wPlaces =
                                          workplace.sll_tietoni_employment_hierarchylevel_1
                                        if (wPlaces.includes(',')) {
                                          const nums = wPlaces
                                            .split(',')
                                            .map((s: string) => s.trim())
                                            .filter(Boolean)
                                            .map(Number)
                                            .filter(Number.isFinite)
                                          return nums.includes(attributevalue)
                                        }
                                        return +wPlaces === +attributevalue
                                      })
                                      .sort((a: any, b: any) => {
                                        const av = a?.els_longname?.trim() ?? null
                                        const bv = b?.els_longname?.trim() ?? null
                                        if (av == null && bv == null) {
                                          return 0
                                        }
                                        if (av == null) {
                                          return 1
                                        }
                                        if (bv == null) {
                                          return -1
                                        }
                                        return collator.compare(av, bv)
                                      })
                                    return filteredWorkplaces
                                  } catch (e) {
                                    return []
                                  }
                                })()
                              : []
                          }
                          getOptionLabel={(option: any) =>
                            typeof option === 'object' ? option.els_longname || '' : ''
                          }
                          isOptionEqualToValue={(option, value) => {
                            if (!value || !option) {
                              return false
                            }
                            if (
                              value.els_soteorganisaatiorekisteriid &&
                              option.els_soteorganisaatiorekisteriid
                            ) {
                              return (
                                option.els_soteorganisaatiorekisteriid ===
                                value.els_soteorganisaatiorekisteriid
                              )
                            }
                            return option.els_longname === value.els_longname
                          }}
                          renderOption={(props, option: any) => (
                            <li {...props}>
                              <div>
                                <div>{option.els_longname}</div>
                              </div>
                            </li>
                          )}
                          filterOptions={(options, { inputValue }) => {
                            const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                            setSearchQuery(cleanedInput)
                            return options.filter(option =>
                              option.els_longname.toLowerCase().includes(cleanedInput.toLowerCase())
                            )
                          }}
                          onChange={(event, newValue) => {
                            if (newValue && typeof newValue === 'object') {
                              const cleanedWorkplace = JSON.stringify({
                                els_longname: newValue.els_longname
                                  .trim()
                                  .replace(/[^\w\s\-åäöÅÄÖ()]/g, ''),
                                els_soteorganisaatiorekisteriid:
                                  newValue.els_soteorganisaatiorekisteriid
                              })
                              onChange(cleanedWorkplace)
                            } else {
                              onChange('')
                            }
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder={
                                sector
                                  ? 'Valitse tai kirjoita hakuun...'
                                  : 'Valitse ensin työnantajasektori'
                              }
                              fullWidth
                              variant='outlined'
                              size='small'
                              disabled={!sector}
                            />
                          )}
                          disabled={!sector}
                          noOptionsText={
                            !sector ? 'Valitse ensin työnantajasektori' : 'Ei vaihtoehtoja'
                          }
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container item>
                <Grid item xs={12}>
                  <FormLabel>Toimiala</FormLabel>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name='els_tyosuhdetoimiala'
                    control={control}
                    defaultValue=''
                    render={({ field: { onChange, value } }) => {
                      let parsedValue = null
                      if (value) {
                        if (typeof value === 'string') {
                          try {
                            parsedValue = JSON.parse(value)
                          } catch {
                            parsedValue = null
                          }
                        } else if (typeof value === 'number') {
                          // If it's a number, find the matching option
                          const option = metaData?.toimialat?.find(
                            (t: any) => t.attributevalue === value
                          )
                          parsedValue = option || null
                        }
                      }
                      return (
                        <Autocomplete
                          value={parsedValue}
                          options={metaData?.toimialat || []}
                          getOptionLabel={(option: any) =>
                            typeof option === 'object' ? option.value : ''
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.attributevalue === value.attributevalue
                          }
                          filterOptions={(options, { inputValue }) => {
                            const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                            return options.filter(option =>
                              option.value.toLowerCase().includes(cleanedInput.toLowerCase())
                            )
                          }}
                          onChange={(event, newValue) => {
                            if (newValue) {
                              const cleanedToimiala = JSON.stringify({
                                attributevalue: newValue.attributevalue,
                                value: newValue.value.trim().replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                              })
                              onChange(cleanedToimiala)
                            } else {
                              onChange('')
                            }
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder='Valitse tai kirjoita hakuun...'
                              fullWidth
                              variant='outlined'
                              size='small'
                            />
                          )}
                          noOptionsText='Ei toimialoja saatavilla'
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Työpaikan sijainti</FormLabel>
                  <Controller
                    name='sll_tyosuhde_sijaintikunta'
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      // Find the selected option object based on value (els_tunnus)
                      console.log('info', 'sijaintikunta value:' + value + 'type:' + typeof value)
                      const selectedOption =
                        value && metaData?.kunnat
                          ? metaData.kunnat.find(
                              (kunta: any) =>
                                (typeof kunta === 'object' ? kunta.els_koodiid : null) === value
                            )
                          : null

                      return (
                        <Autocomplete
                          value={selectedOption || null}
                          options={
                            metaData?.kunnat
                              ?.filter((kunta: any) => typeof kunta === 'object')
                              .sort((a: any, b: any) => {
                                const aLabel = a.els_koodinnimi || a.name || a.value || ''
                                const bLabel = b.els_koodinnimi || b.name || b.value || ''
                                if (aLabel == null && bLabel == null) {
                                  return 0
                                }
                                if (aLabel == null) {
                                  return 1
                                } // nulls last
                                if (bLabel == null) {
                                  return -1
                                }
                                return collator.compare(aLabel, bLabel) // ASC; swap to (bv, av) for DESC
                              }) || []
                          }
                          getOptionLabel={(option: any) =>
                            typeof option === 'object'
                              ? option.els_koodinnimi || option.name || option.value || ''
                              : ''
                          }
                          isOptionEqualToValue={(option, val) => {
                            if (!option || !val) {
                              return false
                            }
                            return (
                              (typeof option === 'object' ? option.els_koodiid : null) ===
                              (typeof val === 'object' ? val.els_koodiid : null)
                            )
                          }}
                          filterOptions={(options, { inputValue }) => {
                            // Clean search input and filter options based on cleaned input
                            const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ]/g, '')
                            return options.filter((option: any) => {
                              const label =
                                option.els_koodinnimi || option.name || option.value || ''
                              return label.toLowerCase().includes(cleanedInput.toLowerCase())
                            })
                          }}
                          onChange={(event, newValue) => {
                            if (newValue && typeof newValue === 'object') {
                              // Store els_tunnus as the value
                              onChange(newValue.els_koodiid || '')
                            } else {
                              // Clear the sll_tyosuhde_sijaintikunta when selection is cleared
                              onChange('')
                            }
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder='Valitse tai kirjoita hakuun...'
                              fullWidth
                              variant='outlined'
                              size='small'
                              disabled={!selectedJob}
                            />
                          )}
                          noOptionsText='Ei kaupunkeja saatavilla'
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Ammattinimike</FormLabel>
                  <Controller
                    control={control}
                    name='els_ammattinimike'
                    defaultValue=''
                    render={({ field: { onChange, value } }) => {
                      let parsedValue = null
                      if (value) {
                        if (typeof value === 'string' && value.startsWith('{')) {
                          try {
                            parsedValue = JSON.parse(value)
                          } catch {
                            parsedValue = null
                          }
                        } else if (typeof value === 'string') {
                          // If it's just an ID string, find the matching option
                          const option = metaData?.ammattinimikkeet?.find(
                            (a: any) => a.els_koodiid === value
                          )
                          parsedValue = option || null
                        }
                      }
                      return (
                        <Autocomplete
                          value={parsedValue}
                          options={metaData?.ammattinimikkeet || []}
                          getOptionLabel={(option: any) =>
                            typeof option === 'object' ? option.els_koodinnimi : ''
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.els_koodiid === value.els_koodiid
                          }
                          filterOptions={(options, { inputValue }) => {
                            // Clean search input and filter options based on cleaned input
                            const cleanedInput = inputValue.replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                            return options.filter(option =>
                              option.els_koodinnimi
                                .toLowerCase()
                                .includes(cleanedInput.toLowerCase())
                            )
                          }}
                          onChange={(event, newValue) => {
                            if (newValue) {
                              // Clean and set the selected job title in the form
                              const cleanedJobTitle = JSON.stringify({
                                els_koodiid: newValue.els_koodiid,
                                els_koodinnimi: newValue.els_koodinnimi
                                  .trim()
                                  .replace(/[^\w\s\-åäöÅÄÖ()]/g, '')
                              })
                              onChange(cleanedJobTitle)
                            } else {
                              // Clear the job title when selection is cleared
                              onChange('')
                            }
                          }}
                          renderInput={params => (
                            <TextField
                              {...params}
                              placeholder='Valitse tai kirjoita hakuun...'
                              fullWidth
                              variant='outlined'
                              size='small'
                            />
                          )}
                          noOptionsText='Ei ammattinimikkeitä saatavilla'
                        />
                      )
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Onko tämä päätoiminen työpaikkasi?</FormLabel>

                  <Controller
                    name='els_paatoimi'
                    control={control}
                    defaultValue='yes'
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup onChange={e => onChange(e.target.value)} value={value}>
                        <FormControlLabel value='yes' control={<Radio />} label='Kyllä' />
                        <FormControlLabel value='no' control={<Radio />} label='Ei' />
                      </RadioGroup>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Työajan jakautuminen</FormLabel>
                  <Controller
                    control={control}
                    name='sll_tyoaika'
                    defaultValue='921950000'
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel
                          value='921950000'
                          control={<Radio />}
                          label='Kokoaikainen'
                        />
                        <FormControlLabel
                          value='921950001'
                          control={<Radio />}
                          label='Osa-aikainen'
                        />
                      </RadioGroup>
                    )}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Työsuhteen luonne</FormLabel>
                  <Controller
                    control={control}
                    name='tyosuhdeluonne'
                    defaultValue='toistaiseksi_voimassa'
                    render={({ field }) => (
                      <RadioGroup {...field}>
                        <FormControlLabel
                          value='toistaiseksi_voimassa'
                          control={<Radio />}
                          label='Toistaiseksi voimassa oleva'
                        />
                        <FormControlLabel
                          value='maaraaikainen'
                          control={<Radio />}
                          label='Määräaikainen'
                        />
                      </RadioGroup>
                    )}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Työsuhteen alkupäivä</FormLabel>
                  <Controller
                    control={control}
                    name='els_alkupvm'
                    defaultValue=''
                    render={({ field: { onChange, value } }) => {
                      // Only parse if value is a valid YYYY-MM-DD format string
                      let parsedDate: Date | null = null
                      if (value && typeof value === 'string') {
                        const dateRegex = /^\d{4}-\d{2}-\d{2}$/
                        if (dateRegex.test(value)) {
                          try {
                            const date = new Date(value)
                            // Validate that the date is actually valid
                            if (!isNaN(date.getTime())) {
                              const [year, month, day] = value.split('-').map(Number)
                              if (
                                date.getFullYear() === year &&
                                date.getMonth() === month - 1 &&
                                date.getDate() === day
                              ) {
                                parsedDate = date
                              }
                            }
                          } catch (e) {
                            // Invalid date, keep parsedDate as null
                          }
                        }
                      }

                      return (
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                          <DatePicker
                            label=''
                            value={parsedDate}
                            onChange={newValue => {
                              // Only format if date is complete and reasonable (year >= 1900)
                              // This prevents formatting incomplete dates during keyboard input
                              if (
                                newValue &&
                                newValue instanceof Date &&
                                !isNaN(newValue.getTime()) &&
                                newValue.getFullYear() >= 1900
                              ) {
                                try {
                                  onChange(format(newValue, 'yyyy-MM-dd'))
                                } catch (e) {
                                  // Don't update form value if formatting fails during typing
                                }
                              } else if (!newValue) {
                                // Only clear if explicitly cleared (null/undefined)
                                onChange(null)
                              }
                              // If date is incomplete (year < 1900), don't update form value
                              // This allows user to continue typing without interruption
                            }}
                            onAccept={newValue => {
                              // Handle manual keyboard input - always format on accept
                              if (
                                newValue &&
                                newValue instanceof Date &&
                                !isNaN(newValue.getTime())
                              ) {
                                try {
                                  onChange(format(newValue, 'yyyy-MM-dd'))
                                } catch (e) {
                                  onChange('')
                                }
                              } else if (!newValue) {
                                onChange('')
                              }
                            }}
                            inputFormat='dd.MM.yyyy'
                            mask=''
                            disableMaskedInput
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder='p.k.vvvv tai pp.kk.vvvv'
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                required
                                inputProps={{
                                  ...params.inputProps,
                                  readOnly: false,
                                  placeholder: 'p.k.vvvv tai pp.kk.vvvv'
                                }}
                                onBlur={e => {
                                  // Parse manually typed dates when user leaves the field
                                  const typedValue = e.target.value
                                  if (typedValue) {
                                    const parsedDate = parseFlexibleDate(typedValue)
                                    if (parsedDate) {
                                      onChange(format(parsedDate, 'yyyy-MM-dd'))
                                    }
                                  }
                                  // Call original onBlur if it exists
                                  if (params.inputProps?.onBlur) {
                                    params.inputProps.onBlur(e)
                                  }
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      )
                    }}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12}>
                <Grid item xs={12} md={6}>
                  <FormLabel>Työsuhteen loppupäivä</FormLabel>
                  <Controller
                    control={control}
                    name='els_loppupvm'
                    defaultValue={null}
                    render={({ field: { onChange, value } }) => {
                      // Only parse if value is a valid YYYY-MM-DD format string
                      let parsedDate: Date | null = null
                      if (value && value !== null && typeof value === 'string' && value !== '') {
                        const dateRegex = /^\d{4}-\d{2}-\d{2}$/
                        if (dateRegex.test(value)) {
                          try {
                            const date = new Date(value)
                            // Validate that the date is actually valid
                            if (!isNaN(date.getTime())) {
                              const [year, month, day] = value.split('-').map(Number)
                              if (
                                date.getFullYear() === year &&
                                date.getMonth() === month - 1 &&
                                date.getDate() === day
                              ) {
                                parsedDate = date
                              }
                            }
                          } catch (e) {
                            // Invalid date, keep parsedDate as null
                          }
                        }
                      }

                      return (
                        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fi}>
                          <DatePicker
                            label=''
                            value={parsedDate}
                            onChange={newValue => {
                              // Only format if date is complete and reasonable (year >= 1900)
                              // This prevents formatting incomplete dates during keyboard input
                              if (
                                newValue &&
                                newValue instanceof Date &&
                                !isNaN(newValue.getTime()) &&
                                newValue.getFullYear() >= 1900
                              ) {
                                try {
                                  onChange(format(newValue, 'yyyy-MM-dd'))
                                } catch (e) {
                                  // Don't update form value if formatting fails during typing
                                }
                              } else if (!newValue) {
                                // Only clear if explicitly cleared (null/undefined)
                                onChange(null)
                              }
                              // If date is incomplete (year < 1900), don't update form value
                              // This allows user to continue typing without interruption
                            }}
                            onAccept={newValue => {
                              // Handle manual keyboard input - always format on accept
                              if (
                                newValue &&
                                newValue instanceof Date &&
                                !isNaN(newValue.getTime())
                              ) {
                                try {
                                  onChange(format(newValue, 'yyyy-MM-dd'))
                                } catch (e) {
                                  onChange(null)
                                }
                              } else if (!newValue) {
                                onChange(null)
                              }
                            }}
                            inputFormat='dd.MM.yyyy'
                            mask=''
                            disableMaskedInput
                            renderInput={params => (
                              <TextField
                                {...params}
                                placeholder='p.k.vvvv tai pp.kk.vvvv'
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                required={employmentType === 'maaraaikainen'}
                                inputProps={{
                                  ...params.inputProps,
                                  readOnly: false,
                                  placeholder: 'p.k.vvvv tai pp.kk.vvvv'
                                }}
                                onBlur={e => {
                                  // Parse manually typed dates when user leaves the field
                                  const typedValue = e.target.value
                                  if (typedValue) {
                                    const parsedDate = parseFlexibleDate(typedValue)
                                    if (parsedDate) {
                                      onChange(format(parsedDate, 'yyyy-MM-dd'))
                                    }
                                  }
                                  // Call original onBlur if it exists
                                  if (params.inputProps?.onBlur) {
                                    params.inputProps.onBlur(e)
                                  }
                                }}
                              />
                            )}
                          />
                        </LocalizationProvider>
                      )
                    }}
                  />
                  <ErrorMessage>{errors?.els_loppupvm?.message}</ErrorMessage>
                </Grid>
              </Grid>
            </Grid>
            {submitError && (
              <ErrorMessage>Jotain meni pieleen muokattaessa työpaikkaa</ErrorMessage>
            )}
          </ShadowContainer>
          <Grid
            container
            spacing={3}
            direction='row'
            wrap='wrap-reverse'
            alignItems='center'
            justifyContent='space-between'
            sx={{ paddingTop: '3vh' }}>
            <Grid item xs={12} md={4}>
              <LaakariButton variant='outlined' onClick={closeDialog} disabled={isSubmitting}>
                Takaisin
              </LaakariButton>
            </Grid>
            <Grid item xs={12} md={4}>
              <LaakariButton
                type='submit'
                disabled={isSubmitting || Object.keys(errors).length > 0}>
                Lähetä
              </LaakariButton>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  ) : (
    <LinearProgress />
  )
}

export default UpdateJobForm
